import { money } from './drawerFormat.js';
import {
  VARIANCE_ACTIONS,
  VARIANCE_ACTION_LABELS,
  varianceActionExportLane,
  varianceActionTargetLabel,
} from './varianceActions.js';
import {
  compactEvidenceSnapshot,
  inheritedCpqOverrideFromContext,
  scrubInheritedEvidenceBinding,
} from './varianceActionEvidenceSnapshot.js';

export { mergeVarianceActionPrefill } from './varianceActionPrefillMerge.js';
export {
  compactEvidenceSnapshot,
  inheritedCpqOverrideFromContext,
} from './varianceActionEvidenceSnapshot.js';

export const EMPTY_ARRAY = [];

export const defaultProblemForAction = (actionType) => {
  if (actionType === VARIANCE_ACTIONS.ADD_TO_GM) return 'Product is missing from the GM billing file.';
  if (actionType === VARIANCE_ACTIONS.ADD_TO_CPQ) return 'Product is missing from the CPQ billing file.';
  if (actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM) return 'Product should not be billed on the GM file.';
  if (actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ) return 'Product should not be billed on the CPQ file.';
  if (actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) return 'Incorrect product code is present for this dealer.';
  if (actionType === VARIANCE_ACTIONS.CHANGE_PRICE) return 'Product exists, but the file price needs manual correction.';
  if (actionType === VARIANCE_ACTIONS.CUSTOM) return 'Custom billing correction needed for this product.';
  return '';
};

export const defaultFixForAction = (actionType) => {
  if (actionType === VARIANCE_ACTIONS.ADD_TO_GM) return 'Add this product to the GM file.';
  if (actionType === VARIANCE_ACTIONS.ADD_TO_CPQ) return 'Add this product to the CPQ file.';
  if (actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM) return 'Remove this product from the GM file.';
  if (actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ) return 'Remove this product from the CPQ file.';
  if (actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) return 'Replace the product code with the corrected code.';
  if (actionType === VARIANCE_ACTIONS.CHANGE_PRICE) return 'Change the price on the target billing file.';
  if (actionType === VARIANCE_ACTIONS.CUSTOM) return 'Compile this custom variance into add, remove, or adjust.';
  return '';
};

export const firstNonBlank = (...values) => values.find((value) => (
  value !== undefined
  && value !== null
  && String(value).trim() !== ''
)) || '';

export const normalizeCode = (value) => String(value || '').trim().toUpperCase();

const normalizedBrandText = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z]/g, '');

const brandTokensFromValue = (value) => {
  if (Array.isArray(value)) return value.flatMap(brandTokensFromValue);
  const raw = String(value || '').trim();
  if (!raw) return [];
  const compact = normalizedBrandText(raw);
  const words = raw.toUpperCase();
  const tokens = new Set();
  if (compact.includes('CHEVROLET') || /\bCHEVY\b/.test(words)) tokens.add('C');
  if (compact.includes('BUICK')) tokens.add('B');
  if (compact.includes('GMC')) tokens.add('G');
  if (compact.includes('CADILLAC')) tokens.add('CAD');
  ['CBG', 'BCG', 'CGB', 'BGC', 'GCB', 'GBC', 'CG', 'GC', 'CB', 'BC', 'BG', 'GB', 'CAD', 'C', 'B', 'G']
    .forEach((token) => {
      if (compact === token) tokens.add(token === 'GC' ? 'CG' : token === 'BC' ? 'CB' : token === 'GB' ? 'BG' : token);
    });
  if (tokens.has('C') && tokens.has('B') && tokens.has('G')) tokens.add('CBG');
  if (tokens.has('C') && tokens.has('G')) tokens.add('CG');
  if (tokens.has('C') && tokens.has('B')) tokens.add('CB');
  if (tokens.has('B') && tokens.has('G')) tokens.add('BG');
  return [...tokens];
};

const accountBrandSet = (raw = {}) => new Set([
  ...brandTokensFromValue(raw.brands),
  ...brandTokensFromValue(raw.brandsCsv),
  ...brandTokensFromValue(raw.brandsDisplay),
  ...brandTokensFromValue(raw.brand),
  ...brandTokensFromValue(raw.brandCode),
  ...brandTokensFromValue(raw.gmBrandCd),
  ...brandTokensFromValue(raw.salesforceAccountName),
  ...brandTokensFromValue(raw.accountName),
  ...brandTokensFromValue(raw.name),
  ...brandTokensFromValue(raw.dealerName),
]);

const productBrandSuffix = (productCode = '') => {
  const normalized = normalizeCode(productCode).replace(/_SEC$/, '').replace(/_+$/, '');
  const parts = normalized.split('_').filter(Boolean);
  return parts.length > 2 ? parts[parts.length - 1] : '';
};

export const isPopcornParentProductCode = (productCode = '') => productBrandSuffix(productCode) === 'CBG';

const hasAnyBrand = (account, brands = []) => brands.some((brand) => account.brandSet?.has(brand));

const scorePopcornCbgAccount = (account = {}) => {
  if (hasAnyBrand(account, ['CBG'])) return 120;
  if (hasAnyBrand(account, ['CG', 'CB'])) return 100;
  if (hasAnyBrand(account, ['C'])) return 80;
  if (hasAnyBrand(account, ['B', 'G', 'BG', 'CAD'])) return -20;
  return 0;
};

export const preferredAccountForProduct = ({ productCode, accountOptions = [], currentAccountId = '', currentAccountName = '' } = {}) => {
  const current = accountOptions.find((account) => (
    (currentAccountId && (
      account.salesforceAccountId === currentAccountId
      || account.id === currentAccountId
    ))
    || (currentAccountName && account.salesforceAccountName === currentAccountName)
  )) || null;
  if (!isPopcornParentProductCode(productCode)) return current;
  const ranked = accountOptions
    .map((account, index) => ({ account, index, score: scorePopcornCbgAccount(account) }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index);
  return ranked[0]?.account || null;
};

export const evidenceOverrideFromContext = (context = {}) => {
  const row = context.row || {};
  const isCpqContext = context.source === 'cpq';
  const matchedAppSheetOrderItemId = isCpqContext
    ? firstNonBlank(
      context.matchedAppSheetOrderItemId,
      row.matchedAppSheetOrderItemId,
      row.accountMatch?.orderItemId,
    )
    : '';
  return {
    orderItemId: isCpqContext
      ? matchedAppSheetOrderItemId
      : firstNonBlank(
        context.orderItemId,
        row.orderItemId,
        row.__orderItemId,
        context.source === 'appsheet' ? row.id : '',
      ),
    referenceId: firstNonBlank(
      context.referenceId,
      row.referenceId,
      row.referenceID,
      row.ReferenceId,
    ),
  };
};

const accountMatchFromContext = (context = {}) => {
  const row = context.row || {};
  return row.accountMatch && typeof row.accountMatch === 'object' ? row.accountMatch : {};
};

export const bindingFieldsFromContext = (context = {}) => {
  const row = context.row || {};
  const accountMatch = accountMatchFromContext(context);
  const evidenceOverride = evidenceOverrideFromContext(context);
  const rowKey = firstNonBlank(
    context.sourceRowKey,
    context.rowKey,
    row.sourceRowKey,
    row.rowKey,
    row.storageKey,
    row.fileRowId,
    row.id,
    row.rowNumber,
  );
  const salesforceAccountId = firstNonBlank(
    context.salesforceAccountId,
    row.salesforceAccountId,
    row.accountId,
    accountMatch.salesforceAccountId,
  );
  const salesforceAccountName = firstNonBlank(
    context.salesforceAccountName,
    row.salesforceAccountName,
    row.accountName,
    row.dealerName,
    accountMatch.salesforceAccountName,
  );
  const bindingMethod = firstNonBlank(
    context.bindingMethod,
    accountMatch.matchMethod,
    evidenceOverride.orderItemId || evidenceOverride.referenceId ? 'manual_override' : '',
  );
  return {
    sourceRowKey: rowKey,
    salesforceAccountId,
    salesforceAccountName,
    bindingMethod,
    bindingConfidence: firstNonBlank(accountMatch.confidence, bindingMethod === 'manual_override' ? 100 : ''),
  };
};

export const hasSafeAccountBinding = (context = {}) => {
  if (!context) return false;
  if (context.source === 'appsheet' || context.source === 'dpe') {
    return Boolean(bindingFieldsFromContext(context).salesforceAccountId);
  }
  const accountMatch = accountMatchFromContext(context);
  const confidence = Number(accountMatch.confidence);
  return Boolean(
    accountMatch.salesforceAccountId
    && (
      String(accountMatch.matchStatus || '').toLowerCase() === 'matched'
      || String(accountMatch.matchMethod || '').toLowerCase().includes('manual')
      || confidence >= 90
    ),
  );
};

export const priceFromContextRow = (context = {}) => firstNonBlank(
  context.originalPrice,
  context.originalFilePrice,
  context.row?.originalPrice,
  context.row?.dealerCost,
  context.row?.dealer_cost,
  context.row?.budget,
  context.row?.mrr,
  context.row?.dealerPrice,
  context.price,
);

export const catalogPrice = (row = {}) => firstNonBlank(row.standardPrice, row.price, row.dealerCost, row.mrr);

export const catalogOptions = (items = []) => [...items]
  .filter((item) => item?.productCode)
  .sort((left, right) => String(left.productCode).localeCompare(String(right.productCode)));

export const catalogOptionLabel = (option) => {
  if (typeof option === 'string') return option;
  if (!option) return '';
  const name = option.displayName ? ` - ${option.displayName}` : '';
  const price = catalogPrice(option);
  return `${option.productCode}${name}${price || price === 0 ? ` - ${money(Number(price))}` : ''}`;
};

const rowsFromAccountLookup = (accountLookup = null) => {
  if (Array.isArray(accountLookup)) return accountLookup;
  if (Array.isArray(accountLookup?.rows)) return accountLookup.rows;
  if (Array.isArray(accountLookup?.accounts)) return accountLookup.accounts;
  return [];
};

export const accountOptionsFromAppsheet = (appsheetData = {}, accountLookup = null) => {
  const accounts = Array.isArray(appsheetData?.accounts) ? appsheetData.accounts : [];
  const lookupAccounts = rowsFromAccountLookup(accountLookup || appsheetData?.appSheetAccounts);
  const orderItems = Array.isArray(appsheetData?.orderItems) ? appsheetData.orderItems : [];
  const byId = new Map();
  const add = (raw = {}) => {
    const salesforceAccountId = firstNonBlank(raw.salesforceAccountId, raw.salesforceId, raw.accountId, raw.id);
    const accountName = firstNonBlank(raw.salesforceAccountName, raw.accountName, raw.name, raw.dealerName);
    if (!salesforceAccountId && !accountName) return;
    const key = salesforceAccountId || accountName;
    if (byId.has(key)) return;
    byId.set(key, {
      id: key,
      bac: firstNonBlank(raw.bac, raw.dealerCode),
      salesforceAccountId,
      salesforceAccountName: accountName,
      peopleSoftId: firstNonBlank(raw.peopleSoftId, raw.peoplesoftId),
      brandSet: accountBrandSet(raw),
      label: accountName || 'Salesforce account',
    });
  };
  accounts.forEach(add);
  lookupAccounts.forEach(add);
  orderItems.forEach(add);
  return [...byId.values()].sort((left, right) => left.label.localeCompare(right.label));
};

const numericOrNull = (value) => (
  value === '' || value === undefined || value === null ? null : Number(value)
);

export const buildVarianceActionPayload = ({
  draft,
  context,
  currentUserName,
  form,
  selectedEvidence = null,
  selectedAccount = null,
  selectedCatalogItem = null,
  now = () => new Date().toISOString(),
  idFactory = () => `variance-action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
} = {}) => {
  if (!draft || !context) return null;
  const actionType = draft.actionType;
  const timestamp = now();
  const evidenceContext = selectedEvidence || {};
  const baseBindingFields = bindingFieldsFromContext(context);
  const note = String(form?.note || '').trim();
  const manualOrderItemId = String(form?.manualOrderItemId || '').trim();
  const manualReferenceId = String(form?.manualReferenceId || '').trim();
  const replacementProductCode = String(form?.replacementProductCode || '').trim();
  const customProductCode = String(form?.customProductCode || '').trim();
  const customTargetFile = String(form?.customTargetFile || '').trim();
  const customMutation = String(form?.customMutation || '').trim();
  const allowEvidenceAccountFallback = !isPopcornParentProductCode(context.productCode)
    || Boolean(selectedAccount)
    || Boolean(form?.salesforceAccountId);
  const replacementProductName = actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT
    ? String(selectedCatalogItem?.displayName || '').trim()
    : '';
  const addToBillingHold = actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
    && form?.addToBillingHold !== false;
  const clearInheritedEvidence = Boolean(form?.clearInheritedEvidence);
  const inheritedCpqOverride = inheritedCpqOverrideFromContext(context);
  const shouldUseOriginalCpqCode = actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
    && clearInheritedEvidence
    && inheritedCpqOverride.hasOverride;
  const actionProductCode = shouldUseOriginalCpqCode
    ? inheritedCpqOverride.originalProductCode
    : context.productCode;
  const sourceEvidence = compactEvidenceSnapshot(context.row);
  if (shouldUseOriginalCpqCode) {
    scrubInheritedEvidenceBinding(sourceEvidence);
    sourceEvidence.productCode = inheritedCpqOverride.originalProductCode;
    sourceEvidence.oemProductCode = inheritedCpqOverride.originalProductCode;
    sourceEvidence.normalizedProductCode = inheritedCpqOverride.originalProductCode;
    sourceEvidence.originalCpqProductCode = inheritedCpqOverride.originalProductCode;
    sourceEvidence.rawProductCode = inheritedCpqOverride.originalProductCode;
    sourceEvidence.inheritedProductCode = inheritedCpqOverride.inheritedProductCode;
    sourceEvidence.displayProductCode = inheritedCpqOverride.inheritedProductCode;
    sourceEvidence.hasInheritedDisplayCode = true;
  }
  const blockInheritedBindingFallback = shouldUseOriginalCpqCode
    && !selectedAccount
    && !selectedEvidence
    && !form?.salesforceAccountId;
  const targetSource = actionType === VARIANCE_ACTIONS.CUSTOM ? customTargetFile : undefined;
  const targetLabel = targetSource === 'gm' ? 'GM file' : targetSource === 'cpq' ? 'CPQ file' : varianceActionTargetLabel(actionType);
  const stampedProductNote = note
    ? {
        text: note,
        author: currentUserName,
        createdAt: timestamp,
        productCode: replacementProductCode || customProductCode || actionProductCode || '',
        source: context.source,
        sourceLabel: context.sourceLabel,
        orderItemId: manualOrderItemId,
        referenceId: manualReferenceId,
        effectiveDate: firstNonBlank(
          evidenceContext.row?.effectiveDate,
          evidenceContext.row?.activationDate,
          evidenceContext.row?.orderStartDate,
          context.row?.effectiveDate,
          context.row?.activationDate,
          context.row?.orderStartDate,
        ),
      }
    : null;
  const bindingFields = {
    ...baseBindingFields,
    manualOrderItemId,
    manualReferenceId,
    salesforceAccountId: firstNonBlank(
      selectedAccount?.salesforceAccountId,
      form?.salesforceAccountId,
      allowEvidenceAccountFallback ? evidenceContext.salesforceAccountId : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.salesforceAccountId : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.accountId : '',
      allowEvidenceAccountFallback && !blockInheritedBindingFallback ? baseBindingFields.salesforceAccountId : '',
    ),
    salesforceAccountName: firstNonBlank(
      selectedAccount?.salesforceAccountName,
      form?.salesforceAccountName,
      allowEvidenceAccountFallback ? evidenceContext.salesforceAccountName : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.salesforceAccountName : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.accountName : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.dealerName : '',
      allowEvidenceAccountFallback && !blockInheritedBindingFallback ? baseBindingFields.salesforceAccountName : '',
    ),
    peopleSoftId: firstNonBlank(
      selectedAccount?.peopleSoftId,
      allowEvidenceAccountFallback ? evidenceContext.peopleSoftId : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.peopleSoftId : '',
      allowEvidenceAccountFallback ? evidenceContext.row?.peoplesoftId : '',
      selectedAccount || !allowEvidenceAccountFallback || blockInheritedBindingFallback ? '' : baseBindingFields.peopleSoftId,
    ),
    bindingMethod: selectedAccount
      ? 'manual_account_picker'
      : selectedEvidence
        ? 'manual_picker'
        : blockInheritedBindingFallback
          ? ''
          : baseBindingFields.bindingMethod,
    bindingConfidence: selectedAccount || selectedEvidence
      ? 100
      : blockInheritedBindingFallback
        ? null
        : baseBindingFields.bindingConfidence,
  };
  return {
    id: idFactory(),
    createdAt: timestamp,
    createdBy: currentUserName,
    actionType,
    actionLabel: VARIANCE_ACTION_LABELS[actionType],
    targetLabel,
    targetSource,
    compiledMutation: actionType === VARIANCE_ACTIONS.CUSTOM ? customMutation : undefined,
    exportLane: varianceActionExportLane(actionType, context.source, targetSource),
    source: context.source,
    sourceLabel: context.sourceLabel,
    productName: context.productName,
    productCode: actionProductCode,
    sourceProductCode: shouldUseOriginalCpqCode ? inheritedCpqOverride.originalProductCode : undefined,
    price: context.price,
    originalPrice: priceFromContextRow(context),
    productStatus: context.status,
    problem: String(form?.problem || '').trim(),
    fix: String(form?.fix || '').trim(),
    note,
    replacementProductCode,
    proposedPrice: numericOrNull(form?.proposedPrice),
    customProductCode,
    customPrice: numericOrNull(form?.customPrice),
    manualOrderItemId,
    manualReferenceId,
    ...bindingFields,
    sourceEvidence,
    targetEvidence: selectedEvidence ? {
      ...compactEvidenceSnapshot(selectedEvidence.row),
      source: selectedEvidence.source,
    } : {},
    metadata: {
      ...(stampedProductNote ? { productNote: stampedProductNote } : {}),
      ...(replacementProductName ? { replacementProductName } : {}),
      ...(clearInheritedEvidence ? {
        clearedInheritedEvidence: true,
        inheritanceOverride: 'cleared',
        ...(inheritedCpqOverride.originalProductCode ? { originalCpqProductCode: inheritedCpqOverride.originalProductCode } : {}),
        ...(inheritedCpqOverride.inheritedProductCode ? { inheritedProductCode: inheritedCpqOverride.inheritedProductCode } : {}),
      } : {}),
      ...(actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ ? {
        addToBillingHold,
        ...(addToBillingHold ? {
          billingHoldNote: 'Add to Billing Hold',
          productTextColors: { productName: 'error.main' },
        } : {}),
      } : {}),
    },
  };
};
